import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuthenticatedRequest } from "@/hooks/use-authenticated-request";
import AppLayout from "@/components/layout/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Organization, OrganizationType, Tenant } from "@/types";
import { Search, Building, Mail, Globe, Plus, Edit, Trash } from "lucide-react";
// import { Badge } from "@/components/ui/badge";
// import { Separator } from "@/components/ui/separator";

import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// Organization type options
const organizationTypes: OrganizationType[] = ['Owner', 'Owner\'s Firm', 'Contractor'];

// Form validation schema
const organizationSchema = z.object({
  name: z.string().min(1, "Organization name is required"),
  organizationType: z.enum(['Owner', 'Owner\'s Firm', 'Contractor'] as const),
  webAddress: z.string().url({ message: "Please enter a valid URL" }).optional().or(z.literal("")),
  primaryContactEmail: z.string().email({ message: "Please enter a valid email" }).optional().or(z.literal("")),
});

type OrganizationFormValues = z.infer<typeof organizationSchema>;

// Zod schema for invite tenant admin form
// const inviteTenantAdminSchema = z.object({
//   email: z.string().email("Invalid email address"),
//   tenantId: z.string().uuid("Please select a valid tenant"),
// });
// type InviteTenantAdminFormValues = z.infer<typeof inviteTenantAdminSchema>;

// interface InvitationCreationResponse {
//   message: string;
//   invitationId?: string;
// }

export default function OrganizationsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const authenticatedRequest = useAuthenticatedRequest();
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<OrganizationType | "All">("All");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [deletingOrg, setDeletingOrg] = useState<Organization | null>(null);
  const [_isInviteAdminDialogOpen, _setIsInviteAdminDialogOpen] = useState(false);
  
  // Fetch organizations
  const { data: organizations, isLoading } = useQuery<Organization[]>({
    queryKey: ["/api/organizations", currentUser?.activeTenantContext],
    queryFn: async () => {
      const response = await authenticatedRequest("/api/organizations");
      if (!response.ok) {
        throw new Error("Network response was not ok for organizations");
      }
      return response.json();
    },
    enabled: !!currentUser,
  });
  
  // Fetch tenants (for SuperAdmin invite form dropdown)
  const { data: _tenantsList = [], isLoading: _isLoadingTenants } = useQuery<Tenant[], Error>({
    queryKey: ["/api/tenants/list-for-admin"],
    queryFn: async () => {
      if (!currentUser?.isSuperAdmin) return [];
      const res = await authenticatedRequest('/api/tenants/list-for-admin');
      if (!res.ok) throw new Error("Failed to fetch tenants for admin: " + res.statusText);
      return res.json();
    },
    enabled: !!currentUser?.isSuperAdmin,
  });
  
  // Filter organizations based on search term and type
  const filteredOrganizations = organizations?.filter(org => {
    const typeMatch = typeFilter === "All" || org.organizationType === typeFilter;
    const searchTermMatch = !searchTerm || 
      org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (org.primaryContactEmail && org.primaryContactEmail.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return typeMatch && searchTermMatch;
  });
  
  // Initialize form
  const orgForm = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: "",
      organizationType: "Owner", // Default type
      webAddress: "",
      primaryContactEmail: "",
    },
  });
  
  // const inviteForm = useForm<InviteTenantAdminFormValues>({
  //   resolver: zodResolver(inviteTenantAdminSchema),
  //   defaultValues: {
  //     email: "",
  //     tenantId: "",
  //   },
  // });
  
  // Reset form when opening dialog for a new organization
  const handleNewOrganization = () => {
    orgForm.reset({
      name: "",
      organizationType: "Owner", // Default type
      webAddress: "",
      primaryContactEmail: "",
    });
    setEditingOrg(null);
    setIsDialogOpen(true);
  };
  
  // Set form values when editing an organization
  const handleEditOrganization = (org: Organization) => {
    orgForm.reset({
      name: org.name,
      organizationType: org.organizationType,
      webAddress: org.webAddress || "",
      primaryContactEmail: org.primaryContactEmail || "",
    });
    setEditingOrg(org);
    setIsDialogOpen(true);
  };
  
  // Mutation for creating organization
  const upsertOrgMutation = useMutation<Organization, Error, OrganizationFormValues | { id: string, values: OrganizationFormValues }>({
    mutationFn: async (vars) => {
      const method = typeof vars === 'object' && 'id' in vars ? "PUT" : "POST";
      const endpoint = typeof vars === 'object' && 'id' in vars ? `/api/organizations/${vars.id}` : "/api/organizations";
      const bodyValues = typeof vars === 'object' && 'id' in vars ? vars.values : vars;
      
      // Simplified payload: isTenant is no longer part of OrganizationFormValues or the backend model for organizations
      const payload = bodyValues;

      const response = await authenticatedRequest(endpoint, {
        method,
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || (method === "POST" ? "Failed to create organization" : "Failed to update organization"));
      }
      return response.json();
    },
    onSuccess: (data, variables) => {
      const action = typeof variables === 'object' && 'id' in variables && variables.id ? "Updated" : "Created";
      toast({ title: `Organization ${action}` });
      setEditingOrg(null);
      setIsDialogOpen(false);
      orgForm.reset();
      queryClient.invalidateQueries({ queryKey: ["/api/organizations"] });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });
  
  // Form submission handler
  const onSubmitOrgForm = (values: OrganizationFormValues) => {
    if (editingOrg) {
      upsertOrgMutation.mutate({ id: editingOrg.organizationId, values });
    } else {
      upsertOrgMutation.mutate(values);
    }
  };
  
  // const inviteMutation = useMutation<InvitationCreationResponse, Error, InviteTenantAdminFormValues>({
  //   mutationFn: async (data) => {
  //     const response = await authenticatedRequest("/api/invitations/create", {
  //       method: "POST",
  //       body: JSON.stringify({ 
  //         email: data.email,
  //         tenantId: data.tenantId,
  //         roleToAssign: 'TenantAdmin' // Fixed for SuperAdmin inviting TenantAdmins
  //       }),
  //     });
  //     if (!response.ok) {
  //       const errorData = await response.json();
  //       throw new Error(errorData.message || "Failed to send invitation");
  //     }
  //     return response.json(); 
  //   },
  //   onSuccess: (data) => {
  //     toast({ title: "Invitation Sent", description: data.message });
  //     _setIsInviteAdminDialogOpen(false);
  //     inviteForm.reset();
  //   },
  //   onError: (error) => {
  //     toast({ title: "Invitation Error", description: error.message, variant: "destructive" });
  //   },
  // });
  
  // ADDING DELETEMUTATION HERE
  const deleteOrgMutation = useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      const response = await authenticatedRequest(`/api/organizations/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Failed to delete organization" })); 
        throw new Error(errorData.message || "Failed to delete organization");
      }
    },
    onSuccess: () => {
      toast({
        title: "Organization Deleted",
        description: "The organization has been successfully deleted.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/organizations"] });
      setDeletingOrg(null); 
    },
    onError: (error) => {
      toast({
        title: "Deletion Error",
        description: error.message,
        variant: "destructive",
      });
      setDeletingOrg(null); // Also close dialog on error
    },
  });

  // function onInviteSubmit(values: InviteTenantAdminFormValues) {
  //   inviteMutation.mutate(values);
  // }
  
  // Handler to open delete confirmation
  const handleDeleteConfirmation = (org: Organization) => {
    setDeletingOrg(org);
  };

  // Handler to execute delete
  const confirmDeleteOrganization = () => {
    if (deletingOrg) {
      deleteOrgMutation.mutate(deletingOrg.organizationId);
    }
  };

  return (
    <AppLayout>
      <div className="p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Organizations</h1>
            <p className="text-muted-foreground">Manage contractors, owners, and other organizations</p>
          </div>
          <div className="flex items-center space-x-2">
            <Button onClick={handleNewOrganization} variant="default" className="flex items-center">
              <Plus className="mr-2 h-4 w-4" /> Add Organization
            </Button>
          </div>
        </div>
        
        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={18} />
            <Input
              type="search"
              placeholder="Search organizations..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="min-w-[200px]">
            <Select 
              value={typeFilter}
              onValueChange={(value) => setTypeFilter(value as OrganizationType | "All")}
            >
              <SelectTrigger>
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Types</SelectItem>
                {organizationTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Organizations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            // Loading skeletons
            Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <div className="bg-muted animate-pulse rounded-md h-6 w-3/4" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="bg-muted animate-pulse rounded-md h-4 w-full" />
                    <div className="bg-muted animate-pulse rounded-md h-4 w-2/3" />
                  </div>
                </CardContent>
                <CardFooter>
                  <div className="bg-muted animate-pulse rounded-md h-9 w-full" />
                </CardFooter>
              </Card>
            ))
          ) : filteredOrganizations && filteredOrganizations.length > 0 ? (
            // Organization cards
            filteredOrganizations.map((org) => (
              <Card key={org.organizationId} className="overflow-hidden group hover:shadow-md transition-shadow">
                <div className={`h-2 w-full ${
                  org.organizationType === 'Owner' ? 'bg-blue-500' : 
                  org.organizationType === 'Owner\'s Firm' ? 'bg-purple-500' : 
                  'bg-green-500'
                }`} />
                <CardHeader className="pb-3 border-b border-gray-100">
                  <div className="flex justify-between items-start">
                    <CardTitle className="flex items-start text-lg">
                      <Building className="mr-2 h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="leading-snug">{org.name}</span>
                    </CardTitle>
                    <div className="flex flex-col items-end space-y-1 ml-2 flex-shrink-0">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      org.organizationType === 'Owner' ? 'bg-blue-100 text-blue-800' :
                      org.organizationType === 'Owner\'s Firm' ? 'bg-purple-100 text-purple-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {org.organizationType}
                    </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-3">
                  <div className="space-y-3">
                    {org.primaryContactEmail && (
                      <div className="flex items-center text-sm">
                        <Mail className="mr-2 h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="truncate">{org.primaryContactEmail}</span>
                      </div>
                    )}
                    {org.webAddress && (
                      <div className="flex items-center text-sm">
                        <Globe className="mr-2 h-4 w-4 text-muted-foreground shrink-0" />
                        <a 
                          href={org.webAddress.startsWith('http') ? org.webAddress : `https://${org.webAddress}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline truncate"
                        >
                          {org.webAddress}
                        </a>
                      </div>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="justify-end space-x-2 pt-0 opacity-80 group-hover:opacity-100">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEditOrganization(org)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  {currentUser?.isSuperAdmin && (
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteConfirmation(org)}>
                      <Trash className="h-4 w-4" />
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))
          ) : (
            // Empty state
            <div className="col-span-full text-center py-12">
              <Building className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-center text-gray-500 mt-4">
                No organizations match your filters.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                {(searchTerm || typeFilter !== "All") && (
                  <Button variant="outline" onClick={() => {
                    setSearchTerm("");
                    setTypeFilter("All");
                  }}>
                    Clear Filters
                  </Button>
                )}
                <Button onClick={handleNewOrganization}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Organization
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Organization Form Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingOrg ? "Edit Organization" : "Add Organization"}
            </DialogTitle>
            <DialogDescription>
              {editingOrg 
                ? "Update the organization's information below." 
                : "Fill in the details to add a new organization."}
            </DialogDescription>
          </DialogHeader>
          
          <Form {...orgForm}>
            <form onSubmit={orgForm.handleSubmit(onSubmitOrgForm)} className="space-y-6">
              <FormField
                control={orgForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Acme Corp" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={orgForm.control}
                name="organizationType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an organization type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {organizationTypes.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={orgForm.control}
                name="webAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <Input placeholder="https://example.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      The organization's website URL (optional)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={orgForm.control}
                name="primaryContactEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Primary Contact Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="contact@example.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      The primary email contact for this organization (optional)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    setEditingOrg(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={upsertOrgMutation.isPending}
                >
                  {upsertOrgMutation.isPending ? (editingOrg ? "Updating..." : "Creating...") : (editingOrg ? "Update" : "Create")}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      {deletingOrg && (
        <Dialog open={!!deletingOrg} onOpenChange={(isOpen) => !isOpen && setDeletingOrg(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Deletion</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete the organization "{deletingOrg.name}"? 
                This action cannot be undone and will delete associated tenant data if this is a tenant.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="sm:justify-start pt-4">
              <DialogClose asChild>
                <Button type="button" variant="outline" onClick={() => setDeletingOrg(null)}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                variant="destructive"
                onClick={confirmDeleteOrganization} 
                disabled={deleteOrgMutation.isPending}
              >
                {deleteOrgMutation.isPending ? "Deleting..." : "Delete Organization"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AppLayout>
  );
}
